import React, { useEffect, useRef, useState } from 'react';
import { PiletApi } from '@sharefiledev/sharefile-appshell';
import {
	ShareModalParams,
	storageExtensionSlots,
} from '@sharefiledev/storage-plugin-pilet-sdk';
import { Layout, Spin, theme, Typography } from 'antd';
import { useLocation } from 'react-router-dom';
import { t } from '../../../translate';
import { FILE_EDIT_PATH } from '../../../utils/constants';
import { getContainerRID } from '../../../utils/ridUtils';

const { Content } = Layout;
const { Title } = Typography;

interface WopiEditViewerParams {
	piletApi: PiletApi;
}

interface ResourceSessionData {
	rid: string;
	editurl: string;
	fileViewerConfig?: Record<string, any>;
}

const MESSAGE_IDS = {
	UI_SHARING: 'UI_Sharing',
	BLUR_FOCUS: 'Blur_Focus',
	APP_IS_FRAME_TRUSTED: 'App_IsFrameTrusted',
	HOST_IS_FRAME_TRUSTED: 'Host_IsFrameTrusted',
};

export const WopiEditViewer: React.FC<WopiEditViewerParams> = ({ piletApi }) => {
	const { token } = theme.useToken();
	const location = useLocation();
	const iframeRef = useRef<HTMLIFrameElement | null>(null);

	const [error, setError] = useState<boolean>(false);
	const [editUrl, setEditUrl] = useState<string | null>(null);
	const [fileRID, setFileRID] = useState<string | null>(null);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [shareParams, setShareParams] = useState<ShareModalParams | null>(null);
	const [modalKey, setModalKey] = useState(0);

	const guid = location.pathname.split(`${FILE_EDIT_PATH}/`)[1];
	const pilet: any = piletApi;

	useEffect(() => {
		if (!guid) {
			setError(true);
			return;
		}

		const dataString = sessionStorage.getItem(guid);
		if (!dataString) {
			setError(true);
			return;
		}

		try {
			const data: ResourceSessionData = JSON.parse(dataString);

			const { editurl, rid, fileViewerConfig } = data;

			let newEditUrl = editurl;

			if (fileViewerConfig) {
				if (fileViewerConfig.hostInstallAddins) {
					newEditUrl += '&sftc=1';
					newEditUrl += '&host_install_addins=' + fileViewerConfig.hostInstallAddins;
				}
			}

			setEditUrl(newEditUrl);
			setFileRID(rid);
		} catch {
			setError(true);
		}
	}, [guid]);

	useEffect(() => {
		if (iframeRef.current && editUrl) {
			iframeRef.current.src = editUrl;
			iframeRef.current.onerror = () => setError(true);
		}
	}, [editUrl]);

	useEffect(() => {
		const handleMessage = (event: any) => {
			try {
				if (event.source.parent.origin !== window.origin) {
					return;
				}

				const msg = JSON.parse(event.data);
				const msgId = msg.MessageId;

				if (msgId === MESSAGE_IDS.UI_SHARING) {
					const iframe = document.querySelector('iframe');

					iframe?.contentWindow?.postMessage(
						{
							MessageId: MESSAGE_IDS.BLUR_FOCUS,
							SendTime: Date.now(),
							Values: {},
						},
						window.origin
					);

					if (fileRID) {
						const params: ShareModalParams = {
							containerRID: getContainerRID(fileRID)!,
							fileRID: fileRID!,
						};
						setShareParams(params);
						setModalKey(prevKey => (prevKey + 1) % 10000);
						setIsModalVisible(true);
					}
				}

				if (msgId === MESSAGE_IDS.APP_IS_FRAME_TRUSTED) {
					const iframe = document.querySelector('iframe');
					iframe?.contentWindow?.postMessage(
						{
							MessageId: MESSAGE_IDS.HOST_IS_FRAME_TRUSTED,
							SendTime: Date.now(),
							Values: {
								isTopFrameTrusted: true,
							},
						},
						window.origin
					);
				}
			} catch (e) {}
		};

		window.addEventListener('message', handleMessage);

		return () => {
			window.removeEventListener('message', handleMessage);
		};
	}, [editUrl, guid, fileRID]);

	return (
		<>
			<Layout style={styles.container}>
				<Content style={styles.content}>
					{error ? (
						<Title level={2} type="danger" style={{ color: token.red6 }}>
							{t('WOPIEDITVIEWER.ERROR')}
						</Title>
					) : editUrl ? (
						<iframe
							ref={iframeRef}
							allow="fullscreen"
							style={styles.iframe}
							sandbox="allow-scripts allow-same-origin"
						/>
					) : (
						<Spin size="large" />
					)}
				</Content>
			</Layout>
			{isModalVisible && (
				<pilet.Extension
					key={modalKey}
					name={storageExtensionSlots.ShareModal}
					params={shareParams}
				/>
			)}
		</>
	);
};

const styles: { [key: string]: React.CSSProperties } = {
	container: {
		height: '100vh',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	},
	content: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		width: '100%',
		height: '100%',
	},
	iframe: { width: '100%', height: '100%', border: 'none' },
};
